import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { translations } from './translations';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Helper to extract language from pathname
const getLanguageFromPath = (pathname) => {
  const pathLang = pathname.split('/')[1];
  return ['ru', 'hi', 'zh'].includes(pathLang) ? pathLang : 'en';
};

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [language, setLanguage] = useState(() => {
    // First try to get language from URL
    const pathLang = getLanguageFromPath(location.pathname);
    if (pathLang !== 'en') {
      return pathLang;
    }
    // Then check localStorage
    const savedLanguage = localStorage.getItem('preferredLanguage');
    return savedLanguage || 'en';
  });

  // Sync language with URL changes
  useEffect(() => {
    const pathLang = getLanguageFromPath(location.pathname);
    if (pathLang !== language) {
      setLanguage(pathLang);
    }
  }, [location.pathname, language]);

  useEffect(() => {
    // Save language preference to localStorage
    localStorage.setItem('preferredLanguage', language);
    
    // Update document lang attribute
    document.documentElement.lang = language;
    
    // Update document title
    const title = translations[language]?.pageTitle || translations['en']?.pageTitle;
    if (title) {
      document.title = title;
    }
  }, [language]);

  const t = (key) => {
    return translations[language]?.[key] || translations['en']?.[key] || key;
  };

  const changeLanguage = (newLanguage) => {
    if (translations[newLanguage]) {
      setLanguage(newLanguage);
      // Navigate to the language-specific route
      const currentPath = location.pathname;
      const pathWithoutLang = currentPath.replace(/^\/(ru|hi|zh)/, '');
      const newPath = newLanguage === 'en' ? pathWithoutLang || '/' : `/${newLanguage}${pathWithoutLang || ''}`;
      navigate(newPath);
    }
  };

  const value = {
    language,
    changeLanguage,
    t,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
